import {
  FOC_TOUR_LABEL_PLANNING_P_TACTICS_WORKFLOW,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
  focPlanningPRequirement,
  focPlanningPTourNavVisible,
  type FocPlanningPTourStepDef,
} from './focPlanningPTourTypes';
import { FOC_PLANNING_P_TACTICS_WORKFLOW_TOUR_HREF } from './focPlanningPTourRegistry';

export {
  FOC_PLANNING_P_TACTICS_WORKFLOW_TOUR_HREF,
  focPlanningPTourNavVisible,
};

/** Prepare Tactics phase (index 2) hosts 234 / 215 / 215A. */
export const FOC_PLANNING_P_TACTICS_WORKFLOW_TOUR_STEPS: FocPlanningPTourStepDef[] =
  [
    {
      id: 'sectionIntro',
      phaseIndex: 2,
      title: '10.5 Tactics, Resource, and Safety Workflow',
      description:
        'Prep for Tactics and related Planning P phases support strategy and tactics development, ICS 215 resource planning, and ICS 215A safety analysis with objective-to-assignment traceability.',
    },
    focPlanningPRequirement({
      id: 'pp009',
      requirementId: 'FOC-PP-009',
      name: 'Strategy and Tactics Development',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      tour: 'ics234-card',
      widget: 'workAnalysisMatrix',
      description:
        'PRATUS supports creation, editing, sequencing, and linkage of strategies, tactics, objectives, resources, hazards, mitigations, assignments, map features, and operational products through the ICS 234 work analysis matrix.',
    }),
    focPlanningPRequirement({
      id: 'pp009a',
      requirementId: 'FOC-PP-009a',
      name: 'ICS 234 Workflow',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      tour: 'planning-form-card',
      widget: 'workAnalysisMatrix',
      description:
        'Authorized users can create, edit, link, and track strategies and tactics through the ICS 234 workflow on the Prep for Tactics and Tactics Meeting phases.',
    }),
    focPlanningPRequirement({
      id: 'pp009b',
      requirementId: 'FOC-PP-009b',
      name: 'Objective-to-Assignment Traceability',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      tour: 'ics234-card',
      widget: 'workAnalysisMatrix',
      description:
        'PRATUS maintains traceability between incident objectives, strategy, tactic, resource requirement, hazard/mitigation, assignment, and applicable ICS 204 work assignment through linked Planning P products.',
    }),
    focPlanningPRequirement({
      id: 'pp009c',
      requirementId: 'FOC-PP-009c',
      name: 'Tactic-Only Path',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      description:
        'PRATUS supports an authorized tactic-only workflow when incident conditions do not require a formal strategy entry, while retaining the ability to use a complete ICS 234 workflow when required.',
    }),
    focPlanningPRequirement({
      id: 'pp010',
      requirementId: 'FOC-PP-010',
      name: 'ICS 215 Resource Planning Workflow',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      tour: 'ics215-card',
      widget: 'operationalPlanningWorksheet',
      description:
        'PRATUS supports resource requirements within the ICS 215 workflow and links resource needs to draft next-operational-period organization, work assignments, logistical feasibility, finance considerations, resource status, and authorized resource-request processes.',
    }),
    focPlanningPRequirement({
      id: 'pp010a',
      requirementId: 'FOC-PP-010a',
      name: 'Direct Resource Capture',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      tour: 'planning-form-card',
      widget: 'operationalPlanningWorksheet',
      description:
        'Authorized users can add resource needs directly within applicable ICS 215 work assignments or planning blocks without duplicative entry in a separate generic resource screen.',
    }),
    focPlanningPRequirement({
      id: 'pp010b',
      requirementId: 'FOC-PP-010b',
      name: 'Resource Request Timing',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      description:
        'PRATUS supports development of ICS 213RR requirements after Tactics Meeting feedback has established operational, logistical, and financial feasibility. The platform does not require premature resource ordering before tactics and support requirements are reviewed.',
    }),
    focPlanningPRequirement({
      id: 'pp011',
      requirementId: 'FOC-PP-011',
      name: 'ICS 215A Safety Workflow',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      tour: 'ics215a-card',
      widget: 'iapSafetyAnalysis',
      description:
        'PRATUS links ICS 215A hazards, risk considerations, mitigations, and safety decisions to applicable ICS 215 operational elements and feeds relevant approved mitigations to ICS 204 special instructions and other appropriate operational products.',
    }),
    focPlanningPRequirement({
      id: 'user009',
      requirementId: 'US-ER009',
      name: 'Manage Hazard Analysis (ICS-215A)',
      disposition: IOC_CARRYOVER_DISPOSITION,
      phaseIndex: 2,
      tour: 'planning-form-card',
      widget: 'iapSafetyAnalysis',
      description:
        'As a user with permission, view and edit the ICS-215A (Safety Analysis), mapping specific hazards and mitigations to the tactical work assignments defined in the unified planning interface.',
    }),
    focPlanningPRequirement({
      id: 'pp011a',
      requirementId: 'FOC-PP-011a',
      name: 'Multiple Hazard Entries',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      tour: 'ics215a-card',
      widget: 'iapSafetyAnalysis',
      description:
        'PRATUS supports multiple hazard-risk-mitigation entries for a single operational element and renders entries in the official export format using appropriate continuation behavior.',
    }),
    focPlanningPRequirement({
      id: 'pp011b',
      requirementId: 'FOC-PP-011b',
      name: 'Human Safety Decisions',
      disposition: FOC_REQUIRED_DISPOSITION,
      phaseIndex: 2,
      description:
        'PRATUS may provide decision support, warnings, or assistance for safety analysis when authorized; the responsible human decision-maker retains responsibility for safety decisions, approval, and release. This walkthrough does not run AI generation.',
    }),
    {
      id: 'focFeedback',
      title: 'FOC Feedback Notes',
      description: focFeedbackPopoverHtml(
        FOC_TOUR_LABEL_PLANNING_P_TACTICS_WORKFLOW
      ),
    },
  ];

export function focPlanningPTacticsWorkflowTourStepIds(): string[] {
  return FOC_PLANNING_P_TACTICS_WORKFLOW_TOUR_STEPS.map((step) => step.id);
}
